import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, RequireModule, RequireRoles, RequireSuperAdmin } from "@/components/ProtectedRoute";
import { ControlLayout } from "@/components/layout/ControlLayout";

import Login from "@/pages/Login";
import SetPassword from "@/pages/SetPassword";
import Dashboard from "@/pages/Dashboard";
import BrockiList from "@/pages/BrockiList";
import BrockiDetail from "@/pages/BrockiDetail";
import InterneQA from "@/pages/InterneQA";
import MfaRecovery from "@/pages/MfaRecovery";
import Fundstuecke from "@/pages/Fundstuecke";
import Moderation from "@/pages/Moderation";
import Freigaben from "@/pages/Freigaben";
import AiJobs from "@/pages/AiJobs";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import Integrations from "@/pages/Integrations";
import SystemStatus from "@/pages/SystemStatus";
import Onboarding from "@/pages/Onboarding";
import Research from "@/pages/Research";
import AzMaster from "@/pages/AzMaster";
import Cockpit from "@/pages/Cockpit";
import Kategorien from "@/pages/Kategorien";
import Regionen from "@/pages/Regionen";
import Standorte from "@/pages/Standorte";
import Anfragen from "@/pages/Anfragen";
import PlaeneNutzung from "@/pages/PlaeneNutzung";
import AbosRechnungen from "@/pages/AbosRechnungen";
import Bewertungen from "@/pages/Bewertungen";
import StorageMigration from "@/pages/StorageMigration";
import MedienGlobal from "@/pages/MedienGlobal";
import Support from "@/pages/Support";
import SecurityStatus from "@/pages/SecurityStatus";
import SalesDashboard from "@/pages/SalesDashboard";
import SalesCandidates from "@/pages/SalesCandidates";
import SalesPipeline from "@/pages/SalesPipeline";
import SalesOffers from "@/pages/SalesOffers";
import SalesClaims from "@/pages/SalesClaims";
import DataImport from "@/pages/DataImport";
import ImportBetrieb from "@/pages/ImportBetrieb";
import Verzeichnispruefung from "@/pages/Verzeichnispruefung";

const wrap = (module, el) => <RequireModule module={module}>{el}</RequireModule>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/passwort-setzen" element={<SetPassword />} />
            <Route path="/cockpit" element={<Cockpit />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ControlLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={wrap("dashboard", <Dashboard />)} />

              {/* Kunden & Brockis */}
              <Route path="research" element={wrap("research", <Research />)} />
              <Route path="az-master" element={<RequireSuperAdmin><AzMaster /></RequireSuperAdmin>} />
              <Route path="brockis" element={wrap("brocki", <BrockiList />)} />
              <Route path="brockis/:id" element={wrap("brocki", <BrockiDetail />)} />
              <Route path="standorte" element={wrap("brocki", <Standorte />)} />
              <Route path="onboarding" element={wrap("onboarding", <Onboarding />)} />

              {/* Inhalte */}
              <Route path="fundstuecke" element={wrap("fundstuecke", <Fundstuecke />)} />
              <Route path="medien" element={wrap("brocki", <MedienGlobal />)} />
              <Route path="anfragen" element={wrap("brocki", <Anfragen />)} />
              <Route path="bewertungen" element={wrap("moderation", <Bewertungen />)} />

              {/* Business */}
              <Route path="plaene" element={wrap("brocki", <PlaeneNutzung />)} />
              <Route path="abos-rechnungen" element={<RequireSuperAdmin><AbosRechnungen /></RequireSuperAdmin>} />

              {/* Verkauf & Wachstum (Commercial Core, Phase 1) */}
              <Route path="verkauf" element={wrap("sales", <SalesDashboard />)} />
              <Route path="verkauf/kandidaten" element={wrap("sales", <SalesCandidates />)} />
              <Route path="verkauf/pipeline" element={wrap("sales", <SalesPipeline />)} />
              <Route path="verkauf/angebote" element={wrap("sales", <SalesOffers />)} />
              <Route path="verkauf/claims" element={wrap("sales", <SalesClaims />)} />

              {/* Admin */}
              <Route path="benutzer" element={wrap("users", <Users />)} />
              <Route path="support" element={<RequireSuperAdmin><Support /></RequireSuperAdmin>} />
              <Route path="freigaben" element={wrap("moderation", <Freigaben />)} />
              <Route path="moderation" element={wrap("moderation", <Moderation />)} />
              <Route path="audit" element={wrap("audit", <AuditLog />)} />

              {/* System */}
              <Route path="integrationen" element={wrap("integrations", <Integrations />)} />
              <Route path="system" element={wrap("system", <SystemStatus />)} />
              <Route path="storage" element={wrap("system", <StorageMigration />)} />
              <Route path="datenimport" element={<RequireSuperAdmin><DataImport /></RequireSuperAdmin>} />
              <Route path="import-betrieb" element={<RequireSuperAdmin><ImportBetrieb /></RequireSuperAdmin>} />
              <Route path="verzeichnispruefung" element={<RequireRoles roles={["super_admin", "admin_ops"]}><Verzeichnispruefung /></RequireRoles>} />

              {/* Sicherheit */}
              <Route path="security-status" element={<RequireSuperAdmin><SecurityStatus /></RequireSuperAdmin>} />
              <Route path="mfa-recovery" element={<RequireSuperAdmin><MfaRecovery /></RequireSuperAdmin>} />

              {/* Kept (not in primary nav) */}
              <Route path="interne-qa" element={<RequireSuperAdmin><InterneQA /></RequireSuperAdmin>} />
              <Route path="kategorien" element={wrap("bridge", <Kategorien />)} />
              <Route path="regionen" element={wrap("bridge", <Regionen />)} />
              <Route path="ai-jobs" element={wrap("ai_jobs", <AiJobs />)} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
